export const analyzePatterns = (trades) => {
  if (!trades || trades.length === 0) return null;

  // === TIME ANALYSIS ===
  const hourlyPnl = {};
  trades.forEach(t => {
    const hour = t.time
      ? parseInt(t.time.split(':')[0])
      : 10;
    if (!hourlyPnl[hour]) hourlyPnl[hour] = { pnl: 0, count: 0 };
    hourlyPnl[hour].pnl += t.pnl;
    hourlyPnl[hour].count++;
  });

  const hourlyArr = Object.entries(hourlyPnl).map(([h, v]) => ({
    hour: parseInt(h),
    label: `${h}:00-${parseInt(h)+1}:00`,
    pnl: v.pnl,
    count: v.count,
    avgPnl: Math.round(v.pnl / v.count)
  }));

  const worstHour = [...hourlyArr].sort((a,b) => a.pnl - b.pnl)[0];
  const bestHour = [...hourlyArr].sort((a,b) => b.pnl - a.pnl)[0];

  // === DAY ANALYSIS ===
  const days = ['Sunday','Monday','Tuesday',
    'Wednesday','Thursday','Friday','Saturday'];
  const dayPnl = {};
  trades.forEach(t => {
    const day = days[new Date(t.date).getDay()];
    if (!dayPnl[day]) dayPnl[day] = { pnl: 0, count: 0 };
    dayPnl[day].pnl += t.pnl;
    dayPnl[day].count++;
  });

  const dayArr = Object.entries(dayPnl).map(([d, v]) => ({
    day: d,
    pnl: v.pnl,
    count: v.count,
    avgPnl: Math.round(v.pnl / v.count)
  }));

  const worstDay = [...dayArr].sort((a,b) =>
    a.avgPnl - b.avgPnl)[0];
  const bestDay = [...dayArr].sort((a,b) =>
    b.avgPnl - a.avgPnl)[0];

  // === EMOTION ANALYSIS ===
  const emotionStats = {};
  trades.forEach(t => {
    const em = t.emotion || 'Unknown';
    if (!emotionStats[em]) {
      emotionStats[em] = { pnl: 0, count: 0, wins: 0, losses: 0 };
    }
    emotionStats[em].pnl += t.pnl;
    emotionStats[em].count++;
    if (t.pnl > 0) emotionStats[em].wins++;
    else emotionStats[em].losses++;
  });

  const emotionArr = Object.entries(emotionStats).map(
    ([em, v]) => ({
      emotion: em,
      pnl: v.pnl,
      count: v.count,
      winRate: Math.round((v.wins/v.count)*100),
      avgPnl: Math.round(v.pnl / v.count)
    })
  );

  const calmStats = emotionStats['Calm'] ||
    emotionStats['calm'] || null;
  const fomoStats = emotionStats['FOMO'] ||
    emotionStats['fomo'] || null;
  const greedyStats = emotionStats['Greedy'] ||
    emotionStats['greedy'] || null;

  // === SETUP ANALYSIS ===
  const setupStats = {};
  trades.forEach(t => {
    const setup = t.setup || 'Unknown';
    if (!setupStats[setup]) {
      setupStats[setup] = { pnl: 0, count: 0, wins: 0 };
    }
    setupStats[setup].pnl += t.pnl;
    setupStats[setup].count++;
    if (t.pnl > 0) setupStats[setup].wins++;
  });

  const setupArr = Object.entries(setupStats).map(
    ([s, v]) => ({
      setup: s,
      pnl: v.pnl,
      count: v.count,
      winRate: Math.round((v.wins/v.count)*100),
    })
  );

  const bestSetup = [...setupArr].sort((a,b) =>
    b.winRate - a.winRate)[0];
  const worstSetup = [...setupArr].sort((a,b) =>
    a.winRate - b.winRate)[0];

  // === SYMBOL ANALYSIS ===
  const symbolStats = {};
  trades.forEach(t => {
    const sym = t.symbol || t.stock || 'Unknown';
    if (!symbolStats[sym]) {
      symbolStats[sym] = { pnl: 0, count: 0, wins: 0 };
    }
    symbolStats[sym].pnl += t.pnl;
    symbolStats[sym].count++;
    if (t.pnl > 0) symbolStats[sym].wins++;
  });

  const symbolArr = Object.entries(symbolStats).map(
    ([s, v]) => ({
      symbol: s,
      pnl: v.pnl,
      count: v.count,
      winRate: Math.round((v.wins/v.count)*100),
    })
  );

  const bestSymbol = [...symbolArr].sort((a,b) =>
    b.pnl - a.pnl)[0];
  const worstSymbol = [...symbolArr].sort((a,b) =>
    a.pnl - b.pnl)[0];

  // === CONSECUTIVE LOSS ANALYSIS ===
  let maxConsecLoss = 0;
  let currentLoss = 0;
  let revengeCount = 0;

  const sorted = [...trades].sort((a,b) =>
    new Date(a.date) - new Date(b.date)
  );

  sorted.forEach((t, i) => {
    if (t.pnl < 0) {
      currentLoss++;
      maxConsecLoss = Math.max(maxConsecLoss, currentLoss);
      if (i > 0 && sorted[i-1].pnl < 0 &&
          sorted[i-1].date === t.date) {
        revengeCount++;
      }
    } else {
      currentLoss = 0;
    }
  });

  // === OVERALL STATS ===
  const totalPnl = trades.reduce((s,t) => s + t.pnl, 0);
  const wins = trades.filter(t => t.pnl > 0);
  const losses = trades.filter(t => t.pnl < 0);
  const winRate = Math.round((wins.length/trades.length)*100);
  const avgWin = wins.length
    ? Math.round(wins.reduce((s,t) => s+t.pnl,0)/wins.length)
    : 0;
  const avgLoss = losses.length
    ? Math.round(losses.reduce((s,t) => s+t.pnl,0)/losses.length)
    : 0;
  const bestTrade = Math.max(...trades.map(t => t.pnl));
  const worstTrade = Math.min(...trades.map(t => t.pnl));

  // === IMPROVEMENT AREAS ===
  const weaknesses = [];
  const strengths = [];

  if (fomoStats && fomoStats.pnl < 0) {
    weaknesses.push(`FOMO trades se ₹${Math.abs(fomoStats.pnl)} ka loss (${fomoStats.count} trades)`);
  }
  if (revengeCount > 0) {
    weaknesses.push(`${revengeCount} revenge trades detected`);
  }
  if (worstDay && worstDay.avgPnl < 0) {
    weaknesses.push(`${worstDay.day} sabse weak din (avg ₹${worstDay.avgPnl})`);
  }
  if (worstHour && worstHour.avgPnl < 0) {
    weaknesses.push(`${worstHour.label} worst time slot`);
  }

  if (calmStats && calmStats.winRate > 50) {
    strengths.push(`Calm hone pe ${calmStats.winRate}% win rate`);
  }
  if (bestSetup && bestSetup.winRate > 60) {
    strengths.push(`${bestSetup.setup} setup mein ${bestSetup.winRate}% win rate`);
  }
  if (bestSymbol && bestSymbol.pnl > 0) {
    strengths.push(`${bestSymbol.symbol} mein ₹${bestSymbol.pnl} profit`);
  }

  return {
    worstHour,
    bestHour,
    hourlyArr,
    worstDay,
    bestDay,
    dayArr,
    emotionArr,
    calmStats,
    fomoStats,
    greedyStats,
    bestSetup,
    worstSetup,
    setupArr,
    bestSymbol,
    worstSymbol,
    symbolArr,
    maxConsecLoss,
    revengeCount,
    totalPnl,
    winRate,
    avgWin,
    avgLoss,
    bestTrade,
    worstTrade,
    totalTrades: trades.length,
    wins: wins.length,
    losses: losses.length,
    weaknesses,
    strengths,
  };
};
